/**
 * Viewer-wide lifecycle signals that bridge top-level bootstrap (main.tsx)
 * and scene components (MirrorScene.tsx).
 *
 * Right now we only expose one: "CPB's preloader has finished covering the
 * scene". Anything visual that needs to wait for the user to actually see it
 * (entrance animations, loading notifications, first-paint metrics) should
 * gate on this.
 *
 * Intentionally DOM-event based rather than a React context because producers
 * (MutationObserver in main.tsx) live outside the React tree, and consumers
 * (useFrame inside GlbBackedMirrorGroup) don't need re-renders — they read a
 * ref-like snapshot via `isCpbLoaderHidden()` and subscribe once via
 * `onCpbLoaderHidden` for the flip.
 */

const LOADER_HIDDEN_EVENT = "maytrix:cpb-loader-hidden";
const REVEAL_COMPLETE_EVENT = "maytrix:reveal-complete";

let cpbLoaderHidden = false;
let revealComplete = false;

/** Called once by main.tsx when the preloader becomes invisible or detaches. */
export function markCpbLoaderHidden(): void {
  if (cpbLoaderHidden) return;
  cpbLoaderHidden = true;
  document.dispatchEvent(new CustomEvent(LOADER_HIDDEN_EVENT));
}

/** Snapshot read — safe in useFrame / synchronous code. */
export function isCpbLoaderHidden(): boolean {
  return cpbLoaderHidden;
}

/**
 * Subscribe to the flip. Fires immediately (via microtask) if the loader is
 * already hidden when you subscribe, so consumers don't miss the signal on
 * late mount. Returns an unsubscribe fn.
 */
export function onCpbLoaderHidden(cb: () => void): () => void {
  if (cpbLoaderHidden) {
    queueMicrotask(cb);
    return () => undefined;
  }
  const handler = () => cb();
  document.addEventListener(LOADER_HIDDEN_EVENT, handler, { once: true });
  return () => document.removeEventListener(LOADER_HIDDEN_EVENT, handler);
}

/** Called by the reveal animation when its final phase has finished. */
export function markRevealComplete(): void {
  if (revealComplete) return;
  revealComplete = true;
  document.dispatchEvent(new CustomEvent(REVEAL_COMPLETE_EVENT));
}

/** Snapshot read — used by CursorSteerCamera to gate its input. */
export function isRevealComplete(): boolean {
  return revealComplete;
}

/** Subscribe to the one-shot reveal-complete signal. Same semantics as `onCpbLoaderHidden`. */
export function onRevealComplete(cb: () => void): () => void {
  if (revealComplete) {
    queueMicrotask(cb);
    return () => undefined;
  }
  const handler = () => cb();
  document.addEventListener(REVEAL_COMPLETE_EVENT, handler, { once: true });
  return () => document.removeEventListener(REVEAL_COMPLETE_EVENT, handler);
}
